/**
 * Toasts for background actions.
 *
 * The rule (docs/08-uiux.md §7): a toast for something that happened away from
 * the cursor, inline confirmation for something in context. Never both — the
 * legacy UI did both and the result was a receptionist ignoring all of it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'
import { cn, uuid } from '@/lib/utils'

type Tone = 'success' | 'error' | 'info'

interface Toast {
  id: string
  tone: Tone
  title: string
  description?: string
}

interface ToastContextValue {
  show: (toast: Omit<Toast, 'id'>) => void
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
  info: (title: string, description?: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const ICONS: Record<Tone, ReactNode> = {
  success: <CheckCircle2 className="h-4 w-4 text-[var(--success)]" aria-hidden />,
  error: <AlertCircle className="h-4 w-4 text-[var(--danger)]" aria-hidden />,
  info: <Info className="h-4 w-4 text-[var(--info)]" aria-hidden />,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const show = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = uuid()
    setToasts((current) => [...current, { ...toast, id }])
  }, [])

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (title, description) => show({ tone: 'success', title, description }),
      error: (title, description) => show({ tone: 'error', title, description }),
      info: (title, description) => show({ tone: 'info', title, description }),
    }),
    [show],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 end-4 z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
        role="region"
        aria-label="Notifications"
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    // Errors stay until dismissed: an error a receptionist did not read is an
    // error that will be reported as "it just did nothing".
    if (toast.tone === 'error') return
    const timer = setTimeout(onDismiss, 4500)
    return () => clearTimeout(timer)
  }, [toast.tone, onDismiss])

  return (
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
      className={cn(
        'fade-in pointer-events-auto flex items-start gap-2.5 rounded-[14px] border bg-[var(--surface)] px-3.5 py-3 shadow-[var(--shadow-lg)]',
        toast.tone === 'error' ? 'border-[var(--danger)]' : 'border-[var(--border-strong)]',
      )}
    >
      <span className="mt-0.5">{ICONS[toast.tone]}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium">{toast.title}</p>
        {toast.description && (
          <p className="mt-0.5 text-[12px] text-[var(--text-muted)]">{toast.description}</p>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="rounded p-0.5 text-[var(--text-muted)] hover:bg-[var(--surface-2)]"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a ToastProvider')
  return context
}
